<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div
    ref="root"
    class="expansion"
    :class="{ expanded: isExpanded }"
  >
    <div
      v-if="(title || hasHeaderSlot) && !hideHeader"
      class="expansion__title"
      :class="{ light, 'flex-row-reverse': alignment === 'right' }"
      tabindex="0"
      :aria-label="`${title} ${!isExpanded ? 'aufklappen' : 'zuklappen'}`"
      :aria-expanded="String(isExpanded)"
      @click="isExpanded = !isExpanded"
      @keydown.enter="isExpanded = !isExpanded"
      @keydown.space="isExpanded = !isExpanded"
    >
      <div
        v-if="!hideIcon"
        class="col-auto"
      >
        <i
          class="fa-chevron-down"
          :class="`fa-${light ? 'light' : 'regular'}`"
        />
      </div>
      <template v-if="hasHeaderSlot">
        <slot name="header" :duration="duration" />
      </template>
      <div
        v-else-if="!hideTitle"
        class="col-auto"
        v-html="title"
      />
      <div
        v-if="!hideLine"
        class="col"
      />
    </div>
    <transition
      name="expansion"
      @enter="enter"
      @after-enter="afterEnter"
      @leave="leave"
    >
      <div
        v-show="isExpanded"
        class="expansion__content"
      >
        <slot />
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  // eslint-disable-next-line vue/multi-word-component-names
  name: 'Expansion',

  props: {
    title: {
      type: String,
      default: null,
    },
    hideIcon: {
      type: Boolean,
      default: false,
    },
    hideLine: {
      type: Boolean,
      default: false,
    },
    hideHeader: {
      type: Boolean,
      default: false,
    },
    hideTitle: {
      type: Boolean,
      default: false,
    },
    light: {
      type: Boolean,
      default: false,
    },
    value: {
      type: Boolean,
      default: false,
    },
    alignment: {
      type: String,
      default: 'left',
      validator: (value) => ['left', 'right'].includes(value),
    },
    duration: {
      type: Number,
      default: 0.575,
    },
    lineColor: {
      type: String,
      default: '#EAEAEA',
    },
  },

  data() {
    return {
      isExpanded: this.value,
    };
  },

  computed: {
    hasHeaderSlot() {
      return !!this.$slots.header;
    },
    expanded: {
      get() {
        return this.value;
      },
      set(isExpanded) {
        this.$emit('input', isExpanded);
      },
    },
  },

  watch: {
    value(isExpanded) {
      if (isExpanded !== this.isExpanded) {
        this.isExpanded = this.value;
      }
    },
    isExpanded(isExpanded) {
      this.expanded = isExpanded;
    },
  },

  mounted() {
    this.$refs.root.style.setProperty('--expansion-duration', `${this.duration}s`);
  },

  methods: {
    /* eslint-disable no-param-reassign */
    enter(element) {
      const { width } = getComputedStyle(element);

      element.style.width = width;
      element.style.position = 'absolute';
      element.style.visibility = 'hidden';
      element.style.height = 'auto';

      const { height } = getComputedStyle(element);

      element.style.width = '';
      element.style.position = '';
      element.style.visibility = '';
      element.style.height = '0';

      const heightFix = getComputedStyle(element).height;

      requestAnimationFrame(() => {
        element.style.height = height;
      });

      return heightFix;
    },
    afterEnter(element) {
      element.style.height = 'auto';
    },
    leave(element) {
      const { height } = getComputedStyle(element);

      element.style.height = height;

      const heightFix = getComputedStyle(element).height;

      requestAnimationFrame(() => {
        element.style.height = '0';
      });

      return heightFix;
    },
    /* eslint-enable no-param-reassign */
  },
};
</script>

<style lang="scss">
    :root {
        --expansion-duration: 0.575s;
    }

    .expansion {
        pointer-events: all;

        &, > div {
            will-change: height;
            backface-visibility: hidden;
        }

        &__title {
            @apply flex items-center cursor-pointer;
            font-size: 16px;
            font-weight: 700;

            > div:nth-child(1) {
                color: var(--company-main-primary);
                font-size: 12px;

                > i, > svg {
                    transform: rotate(0deg);
                    transition: transform var(--expansion-duration) ease-in-out
                }
            }

            > div:nth-child(2) {
                padding: 0 10px;
            }

            > div:nth-child(3) {
                position: relative;

                &:after {
                    content: '';
                    position: absolute;
                    left: 0;
                    top: 0;
                    width: 100%;
                    height: 1px;
                    background-color: v-bind(lineColor);
                }
            }

            &.light {
                font-size: 14px;
                font-weight: 400;
            }

            + div {
                padding-top: 5px;
                position: relative;
            }
        }

        &.expanded {
            .expansion__title {
                > div:nth-child(1) {
                    > i, > svg {
                        transform: rotate(-90deg);
                    }
                }
            }
        }
    }

    .expansion-enter-active,
    .expansion-leave-active {
        transition: height var(--expansion-duration) ease-in-out;
        overflow: hidden;
    }

    .expansion-enter,
    .expansion-leave-to {
        height: 0;
    }
</style>
